import { useState } from "react";
import { Sparkles, X, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

interface AISummarySheetProps {
  articleTitle: string;
  articleDescription: string;
  articleId: string;
}

export const AISummarySheet = ({ articleTitle, articleDescription, articleId }: AISummarySheetProps) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const generateSummary = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (summary) {
      setOpen(true);
      return;
    }
    setOpen(true);
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setSummary("Devi effettuare il login per usare questa funzione.");
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [
              {
                role: "user",
                content: `Analizza questo articolo e fornisci esattamente 3 punti chiave sintetici in italiano, formattati come elenco numerato (1. 2. 3.). Sii conciso e diretto.\n\nTitolo: ${articleTitle}\nDescrizione: ${articleDescription}`,
              },
            ],
            skipMemory: true,
          }),
        }
      );

      if (!resp.ok) throw new Error("API error");

      const text = await resp.text();
      const lines = text.split("\n");
      let result = "";
      for (const line of lines) {
        if (line.startsWith("data: ") && line.slice(6).trim() !== "[DONE]") {
          try {
            const parsed = JSON.parse(line.slice(6));
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) result += content;
          } catch {}
        }
      }
      setSummary(result || "Impossibile generare il riassunto.");
    } catch {
      setSummary("Errore nella generazione del riassunto.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <>
      <button
        onClick={generateSummary}
        className="p-1.5 rounded-full hover:bg-primary/10 transition-all duration-300 hover:scale-110 active:scale-90"
        title="AI Summary"
      >
        <Sparkles className="w-3.5 h-3.5 text-primary" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40"
            onClick={handleClose}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-card border-t border-border rounded-t-2xl p-5 pb-[140px] shadow-2xl"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-bold text-foreground">AI Summary</h3>
                </div>
                <button onClick={handleClose} className="p-1 rounded-full hover:bg-muted">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              <p className="text-xs font-medium text-muted-foreground mb-3 line-clamp-1">{articleTitle}</p>

              {loading ? (
                <div className="flex items-center justify-center py-8 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Analisi in corso...</span>
                </div>
              ) : (
                <div className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                  {summary}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
